import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router";
import { ALLERGENS, type Allergen } from "../../../shared/constants";
import { api } from "../../api/client";
import { useMenus } from "../../api/queries";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { formatDeadline, timeLeft } from "../../lib/deadline";
import type { ShellContext } from "../AppShell";
import { mainMenu } from "../menus/status";
import { counts, inCategory, loadProgress, nextOpen, resumeIndex, saveProgress } from "./allergenProgress";

// "Confirmă alergenii" (owner 2026-10-28): one product per screen, through the whole menu, unconfirmed first. For a
// product not confirmed yet, the allergens of its recipe's database foods come already ticked (owner 2026-11-02);
// nothing counts until the restaurant confirms. Confirming is a draft change: the products come back on the public
// menu once the menu is published again. Admin review 2026-11-02: category choice, progress (confirmed / skipped
// for now), resume where the restaurant left, actions pinned at the bottom on phones. Never a collective confirm.

interface Item {
  id: string;
  name: string;
  categoryName: string;
  description: string;
  ingredients: string;
  allergens: Allergen[];
  confirmed: boolean;
  suggested: Allergen[];
  deadlineAt: number | null;
}

export function AllergenWizard() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const tz = membership.restaurant.timezone;
  const canEdit = membership.role !== "staff";
  const qc = useQueryClient();
  const menus = useMenus(rid);
  const list = (menus.data ?? []).filter((m) => !m.archived);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const toast = useToast();
  const mid = menuId ?? mainMenu(menus.data ?? [])?.id ?? null;
  const q = useQuery({
    queryKey: ["nutrition", rid, "wizard", mid],
    queryFn: () =>
      api<{ products: Item[] }>("GET", `/restaurants/${rid}/nutrition/menus/${mid}/allergens`).then(
        (r) => r.products,
      ),
    enabled: !!mid,
    staleTime: Number.POSITIVE_INFINITY, // the order stays while the wizard runs
  });
  const [category, setCategory] = useState<string | null>(null);
  const all = q.data ?? [];
  const items = inCategory(all, category);
  const [i, setI] = useState<number | null>(null);
  const [picked, setPicked] = useState<Set<Allergen>>(new Set());
  const [done, setDone] = useState<Set<string>>(new Set());
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Resume where the restaurant left this menu (kept in the browser), once its products are loaded.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!mid || !q.data || loadedFor.current === mid) return;
    loadedFor.current = mid;
    const saved = loadProgress(rid, mid);
    setSkipped(new Set(saved.skipped));
    setDone(new Set());
    setCategory(null);
    setI(resumeIndex(q.data, saved));
  }, [mid, q.data, rid]);

  const idx = i ?? 0;
  const item = items[idx];
  useEffect(() => {
    if (mid && loadedFor.current === mid) {
      saveProgress(rid, mid, { at: item?.id ?? null, skipped: [...skipped] });
    }
  }, [mid, rid, item?.id, skipped]);

  useEffect(() => {
    setPicked(
      new Set(item ? (item.confirmed ? item.allergens : [...item.allergens, ...item.suggested]) : []),
    );
    setError(null);
  }, [item]);

  async function confirm() {
    if (!item || !mid || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api("PUT", `/restaurants/${rid}/nutrition/menus/${mid}/products/${item.id}/allergens`, {
        allergens: [...picked],
      });
      const nextDone = new Set(done).add(item.id);
      const nextSkipped = new Set(skipped);
      nextSkipped.delete(item.id);
      setDone(nextDone);
      setSkipped(nextSkipped);
      await qc.invalidateQueries({ queryKey: ["nutrition", rid, "allergens"] });
      setI(nextOpen(items, idx, nextDone, nextSkipped));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function skip() {
    if (!item) return;
    const nextSkipped = new Set(skipped).add(item.id);
    setSkipped(nextSkipped);
    setI(nextOpen(items, idx, done, nextSkipped));
  }

  const c = counts(items, done, skipped);
  const finished = items.length > 0 && idx >= items.length;
  const isDone = !!item && (item.confirmed || done.has(item.id));
  const categories = [...new Set(all.map((x) => x.categoryName))];
  const leftIn = (name: string | null) => {
    const k = counts(inCategory(all, name), done, skipped);
    return k.left + k.skipped;
  };
  const open = c.left + c.skipped;
  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <Link to="/app/nutrition" className="caption">
          ← Valori nutriționale
        </Link>
        <h1 className="h1">Confirmă alergenii</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          Un produs pe rând, întâi cele neconfirmate. Pornim de la estimarea Meniova (din rețeta tipică):
          verifici că se potrivește cu rețeta ta, corectezi și confirmi. Poți omite și reveni oricând.
        </p>
        {mid && all.some((x) => !x.confirmed && !x.suggested.length) && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="caption muted">
              Unele produse nu au încă o estimare (de exemplu, adăugate manual).
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={estimating}
              onClick={async () => {
                setEstimating(true);
                try {
                  const r = await api<{ started: boolean }>(
                    "POST",
                    `/restaurants/${rid}/nutrition/menus/${mid}/estimate`,
                  );
                  toast(
                    r.started
                      ? "Estimăm alergenii în fundal; reîncarcă pagina peste un minut."
                      : "Nu e nimic de estimat acum (sau o analiză e deja în curs).",
                  );
                } catch (e) {
                  toast(e instanceof Error ? e.message : String(e));
                } finally {
                  setEstimating(false);
                }
              }}
            >
              Estimează alergenii
            </button>
          </div>
        )}
      </div>
      {(list.length > 1 || categories.length > 1) && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {list.length > 1 && (
            <div className="field" style={{ flex: "1 1 220px", maxWidth: 360 }}>
              <label className="field-label" htmlFor="aw-menu">
                Meniul
              </label>
              <select
                id="aw-menu"
                className="field-input"
                value={mid ?? ""}
                onChange={(e) => {
                  setMenuId(e.target.value);
                  setI(null);
                }}
              >
                {list.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {categories.length > 1 && (
            <div className="field" style={{ flex: "1 1 220px", maxWidth: 360 }}>
              <label className="field-label" htmlFor="aw-cat">
                Categoria
              </label>
              <select
                id="aw-cat"
                className="field-input"
                value={category ?? ""}
                onChange={(e) => {
                  const next = e.target.value || null;
                  setCategory(next);
                  setI(resumeIndex(inCategory(all, next), { at: null, skipped: [...skipped] }, done));
                }}
              >
                <option value="">Toate categoriile ({leftIn(null)} de confirmat)</option>
                {categories.map((name) => (
                  <option key={name} value={name}>
                    {name} ({leftIn(name)} de confirmat)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      {items.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="caption" aria-live="polite">
            <strong style={{ fontWeight: 600 }}>
              {c.confirmed} din {c.total} confirmate
            </strong>
            {c.skipped ? ` · ${c.skipped} omise pentru moment` : ""}
            {c.left ? ` · ${c.left} rămase` : ""}
          </span>
          <div
            className="progress"
            role="progressbar"
            aria-label="Produse cu alergenii confirmați"
            aria-valuenow={c.confirmed}
            aria-valuemin={0}
            aria-valuemax={c.total}
          >
            <div style={{ width: `${c.total ? Math.round((c.confirmed / c.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}
      {q.isError && <Alert tone="error">{q.error.message}</Alert>}
      {q.isSuccess && all.length === 0 && (
        <section className="card empty">
          <p style={{ margin: 0 }}>Meniul nu are încă produse.</p>
        </section>
      )}
      {finished && (
        <section className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Alert
            tone={open ? "warning" : "success"}
            title={
              open
                ? `${open === 1 ? "1 produs" : `${open} produse`} fără alergeni confirmați`
                : "Toți alergenii sunt confirmați"
            }
          >
            {open
              ? "Le poți relua oricând. Confirmările se salvează în ciornă și ajung la clienți după ce publici meniul."
              : "Confirmările se salvează în ciornă: publică meniul ca să ajungă la clienți."}
          </Alert>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {open > 0 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setI(resumeIndex(items, { at: null, skipped: [] }, done))}
              >
                Reia produsele omise
              </button>
            )}
            <Link
              to={mid ? `/app/products?menu=${mid}&publish=1` : "/app/products"}
              className={`btn ${open ? "btn-secondary" : "btn-primary"}`}
            >
              Publică meniul
            </Link>
          </div>
        </section>
      )}
      {item && !finished && (
        <section className="card wizard-card" aria-labelledby="aw-h">
          <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span className="muted caption">
              Produsul {idx + 1} din {items.length} · {item.categoryName}
            </span>
            {isDone ? (
              <span className="caption" style={{ color: "var(--color-success)", fontWeight: 600 }}>
                Confirmat
              </span>
            ) : skipped.has(item.id) ? (
              <span className="caption muted">Omis pentru moment</span>
            ) : null}
          </div>
          <h2 id="aw-h" className="h2" style={{ margin: 0, overflowWrap: "anywhere" }}>
            {item.name}
          </h2>
          {(item.description || item.ingredients) && (
            <p className="muted" style={{ margin: 0 }}>
              {[item.description, item.ingredients].filter(Boolean).join(" · ")}
            </p>
          )}
          {!isDone && item.deadlineAt && (
            <p className="caption" style={{ margin: 0 }}>
              {item.deadlineAt <= Date.now() ? (
                <strong style={{ fontWeight: 600 }}>
                  Termen depășit ({formatDeadline(item.deadlineAt, tz)}): produsul apare „indisponibil” în
                  meniul public până confirmi și publici.
                </strong>
              ) : (
                <>
                  <strong style={{ fontWeight: 600 }}>Termen: {formatDeadline(item.deadlineAt, tz)}</strong>{" "}
                  (ora restaurantului, {timeLeft(item.deadlineAt, Date.now())}). După termen produsul apare
                  „indisponibil” până confirmi și publici; termenul nu repornește.
                </>
              )}
            </p>
          )}
          {item.suggested.length > 0 && !isDone && (
            <Alert tone="info" title="Sugestii din rețetă, încă neconfirmate">
              Ingredientele din rețetă conțin:{" "}
              {item.suggested.map((a) => ALLERGENS.find(([code]) => code === a)?.[1] ?? a).join(", ")}. Le-am
              bifat ca sugestie; nu contează până nu confirmi. Adaugă ce lipsește: lipsa unui alergen din
              datele unui ingredient nu înseamnă că produsul nu îl conține.
            </Alert>
          )}
          <fieldset style={{ border: 0, margin: 0, padding: 0 }} disabled={!canEdit}>
            <legend className="field-label">Alergeni (lista UE de 14)</legend>
            <div className="check-grid">
              {ALLERGENS.map(([code, label]) => (
                <label key={code} className="checkline">
                  <input
                    type="checkbox"
                    checked={picked.has(code)}
                    onChange={(e) =>
                      setPicked((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(code);
                        else n.delete(code);
                        return n;
                      })
                    }
                  />
                  <span>
                    {label}
                    {item.suggested.includes(code) ? (
                      <span className="muted"> · sugerat din rețetă</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {error && (
            <Alert tone="error" live>
              {error}
            </Alert>
          )}
          <div className="wizard-actions">
            <button
              type="button"
              className="icon-btn"
              aria-label="Produsul anterior"
              disabled={idx === 0}
              onClick={() => setI(idx - 1)}
            >
              <ArrowLeft {...ICON} aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-secondary" onClick={skip}>
              Omit pentru moment
            </button>
            {canEdit && (
              <button type="button" className="btn btn-primary" disabled={saving} onClick={confirm}>
                <Check {...ICON} aria-hidden="true" />
                {saving ? "Se salvează…" : picked.size ? "Confirmă și continuă" : "Fără alergeni · confirmă"}
              </button>
            )}
          </div>
        </section>
      )}
    </>
  );
}
